import React, { useState, useMemo } from 'react';
import { Search, X, Plus } from 'lucide-react';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';

export function ChipMultiSelect({
  options,
  selected,
  onChange,
  placeholder = 'Search or add...',
  allowCustom = true,
  maxItems = 15,
  showSearch = true,
  className,
}) {
  const [search, setSearch] = useState('');

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const lower = search.toLowerCase();
    return options.filter(opt => opt.toLowerCase().includes(lower));
  }, [options, search]);

  const handleToggle = (option) => {
    if (selected.includes(option)) {
      onChange(selected.filter(s => s !== option));
    } else if (selected.length < maxItems) {
      onChange([...selected, option]);
    }
  };

  const handleAddCustom = () => {
    const value = search.trim();
    if (value && !selected.includes(value) && selected.length < maxItems) {
      onChange([...selected, value]);
      setSearch('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCustom();
    }
  };

  const isCustomValue = search.trim() && 
    !options.some(opt => opt.toLowerCase() === search.toLowerCase()) &&
    !selected.some(s => s.toLowerCase() === search.toLowerCase());

  return (
    <div className={cn('space-y-4', className)}>
      {/* Search/Add Input */}
      {showSearch && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="h-12 pl-10 bg-[#151515] border-white/[0.08] focus:border-amber-500/50 text-white placeholder:text-white/30"
            data-testid="chip-search-input"
          />
          {isCustomValue && allowCustom && (
            <button
              type="button"
              onClick={handleAddCustom}
              className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-amber-500/20 text-amber-400 text-xs font-medium rounded-md hover:bg-amber-500/30 transition-colors flex items-center gap-1"
            >
              <Plus className="h-3 w-3" />
              Add "{search.trim()}"
            </button>
          )}
        </div>
      )}

      {/* Selected Chips */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => handleToggle(item)}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-amber-500/20 text-amber-400 text-sm font-medium rounded-lg border border-amber-500/30 hover:bg-amber-500/30 transition-colors"
              data-testid={`selected-chip-${item}`}
            >
              {item}
              <X className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
      )}

      {/* Option Grid */}
      <div className="flex flex-wrap gap-2">
        {filteredOptions
          .filter(opt => !selected.includes(opt))
          .map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => handleToggle(option)}
              disabled={selected.length >= maxItems}
              className={cn(
                'px-4 py-2.5 rounded-lg text-sm font-medium transition-all',
                'bg-[#1a1a1a] border border-white/[0.08] text-white/70',
                'hover:bg-white/[0.08] hover:text-white hover:border-white/20',
                selected.length >= maxItems && 'opacity-50 cursor-not-allowed'
              )}
              data-testid={`option-chip-${option}`}
            >
              {option}
            </button>
          ))}
      </div>

      {/* Count — only show after first selection */}
      {selected.length > 0 && (
        <p className="text-xs text-white/40 text-center">
          {selected.length} selected
        </p>
      )}
    </div>
  );
}
